import { Autocomplete, Button, TextField, List } from "@mui/material";
import debounce from "lodash/debounce";
import React, { useState, useCallback, useRef } from "react";

import useService from "../../../../../../../hooks/useService";

const ListBoxComponent = React.forwardRef(function ListBoxComponent(props, ref) {
  const { children, allowMore, handleMore, ...other } = props;

  return (
    <List role="listbox" ref={ref} {...other}>
      {children}
      {allowMore && (
        <Button onClick={handleMore} className="w-full">
          Show more results...
        </Button>
      )}
    </List>
  );
});

const ApptBillsAutocomplete = ({
  form,
  handleChange,
  handleErrors,
  idx,
  providerPk,
  searchLimitsByIndex,
  searchTermsByIndex,
  servicesOptsByIndex,
  setSearchLimitsByIndex,
  setSearchTermsByIndex,
  setServicesOptsByIndex,
}) => {
  const autocompleteRefs = useRef([]);
  const serviceRepository = useService("repositories/service-repository");
  const [allowMore, setAllowMore] = useState(false);
  const debouncedGetServices = useCallback(
    debounce((value, idx) => getServices(value, idx), 500),
    []
  );

  async function getServices(search, index, limit = 25) {
    if (!search) {
      setServicesOptsByIndex((prev) => {
        const updated = [...prev];
        updated[index] = [];
        return updated;
      });
      setAllowMore(false);
      return;
    }

    try {
      const data = await serviceRepository.query({
        providerId: providerPk,
        search,
        limit,
      });
      const count = await serviceRepository.getTotalCount();

      const formatted = data.map(({ id, serviceCode, serviceName }) => ({
        value: id,
        label: `${serviceCode} - ${serviceName}`,
      }));

      setAllowMore(count >= limit);
      setServicesOptsByIndex((prev) => {
        const updated = [...prev];
        updated[index] = structuredClone(formatted);
        return updated;
      });
    } catch (error) {
      handleErrors(error);
    }
  }

  const handleMore = async () => {
    const newLimit = (searchLimitsByIndex[idx] || 25) + 25;
    setSearchLimitsByIndex((prev) => {
      const updated = [...prev];
      updated[idx] = newLimit;
      return updated;
    });
    handleChange("service", "", idx);
    await getServices(searchTermsByIndex[idx], idx, newLimit);
  };

  return (
    <Autocomplete
      value={servicesOptsByIndex[idx]?.find((opt) => opt.value === form.service) || null}
      options={servicesOptsByIndex[idx] || []}
      ListboxComponent={(listboxProps) => (
        <ListBoxComponent {...listboxProps} allowMore={allowMore} handleMore={handleMore} />
      )}
      fullWidth
      isOptionEqualToValue={(option, value) => option.value === value}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={(el) => (autocompleteRefs.current[idx] = el)}
          label="CPT Code *"
          placeholder="Type something to search..."
          size="small"
          onChange={async ({ target: { value } }) => {
            setSearchTermsByIndex((prev) => {
              const updated = [...prev];
              updated[idx] = value;
              return updated;
            });
            await debouncedGetServices(value, idx, 25);
          }}
        />
      )}
      blurOnSelect
      size="small"
      onChange={(_, val) => handleChange("service", val?.value ?? "", idx)}
    />
  );
};

export default ApptBillsAutocomplete;
